import type { FormikHelpers } from 'formik'
import { Form, Formik } from 'formik'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import { useCreateProductMutation } from '@Store/api/product'
import type { CreateProductDto } from '@Store/types/dto/product/createProductDto'
import FormElement from '@Utils/forms/form-element'
import { IntercomEvent } from '@Utils/helpers/getIntercomMsg'
import useIntercomInternal from '@Utils/hooks/use-intercom-internal'
import Currency from '@Utils/inputs/currency'
import BrandsDropdown from '@Utils/inputs/searchable-dropdown/brands'
import CategoriesDropdown from '@Utils/inputs/searchable-dropdown/categories'
import TagsDropdown from '@Utils/inputs/searchable-dropdown/tags'
import TextInput from '@Utils/inputs/text-input'
import type {
  CreateableDropdownOption,
  DropdownOption,
} from '@Utils/inputs/types'

import ProductSourceBodyWrapper from '../ProductSourceBodyWrapper'

function AddProductsManuallySingle(): JSX.Element {
  const navigate = useNavigate()

  const { trackEventInternal } = useIntercomInternal()
  const submitDataRef = useRef<{
    new_product_name: string
    product_code?: string
    product_cost?: number
    additional_cost?: number
    product_barcode?: string
    category?: DropdownOption
    brand?: DropdownOption
    tags?: DropdownOption[]
  }>()

  const [createProduct, { isLoading, isSuccess, isError, error }] =
    useCreateProductMutation()

  const [selectedBrand, setSelectedBrand] = useState<CreateableDropdownOption>()
  const [selectedCategory, setSelectedCategory] =
    useState<CreateableDropdownOption>()
  const [selectedTags, setSelectedTags] = useState<DropdownOption[]>()

  const initialValues = {
    new_product_name: '',
    category: selectedCategory,
    brand: selectedBrand,
    product_cost: null,
    additional_cost: null,
  }

  const onSubmit = async (e: any) => {
    const createRequest: CreateProductDto = {
      new_product_name: e.new_product_name,
      product_barcode: e.product_barcode,
      product_code: e.product_code,
      product_cost: e.product_cost,
      additional_cost: e.additional_cost,
    }
    if (selectedBrand) {
      if (e.brand) {
        if (e.brand.__isNew) {
          createRequest.brand_name = selectedBrand.label
        } else {
          createRequest.brand_id = parseInt(selectedBrand.value)
        }
      }
    }
    if (selectedCategory) {
      if (e.category) {
        if (e.category.__isNew) {
          createRequest.category_name = selectedCategory.label
        } else {
          createRequest.category_id = parseInt(selectedCategory.value)
        }
      }
    }

    if (e.tags) {
      createRequest.product_tags = e.tags
        .filter((tag: any) => tag !== undefined)
        .map((tag: any) => {
          if (tag.__isNew) {
            return {
              name: tag.value,
            }
          } else {
            return {
              id: parseInt(tag.value),
            }
          }
        })
    }

    if (!e.product_cost) {
      createRequest.product_cost = null
    }
    if (!e.additional_cost) {
      createRequest.additional_cost = null
    }
    submitDataRef.current = {
      new_product_name: e.new_product_name,
      product_code: e.product_code,
      product_cost: e.product_cost,
      additional_cost: e.additional_cost,
      product_barcode: e.product_barcode,
      category: selectedCategory,
      brand: selectedBrand,
      tags: e.tags,
    }

    await createProduct(createRequest)
  }

  const initialPlaceholders = {
    category: 'Type to create or select a category…',
    brand: 'Type to create or select a brand…',
    tags: 'Type to create or select a tag…',
  }

  const clearForm = (
    resetForm: FormikHelpers<any>['resetForm'],
    setFieldValue: FormikHelpers<any>['setFieldValue'],
    setFieldTouched: FormikHelpers<any>['setFieldTouched'],
  ) => {
    resetForm()
    setFieldValue('product_cost', '')
    setFieldTouched('product_cost', false)
    setFieldValue('additional_cost', '')
    setFieldTouched('additional_cost', false)
    setFieldValue('product_code', '')
    setFieldTouched('product_code', false)
    setFieldValue('product_barcode', '')
    setFieldTouched('product_barcode', false)
    setSelectedBrand({
      value: '',
      label: initialPlaceholders.brand,
    })
    setFieldValue('brand', '')
    setFieldTouched('brand', false)
    setSelectedCategory({
      value: '',
      label: initialPlaceholders.category,
    })
    setFieldValue('category', '')
    setFieldTouched('category', false)
    setSelectedTags([])
    setFieldTouched('tags', false)
  }

  useEffect(() => {
    if (isSuccess) {
      trackEventInternal(IntercomEvent['product-added'], {
        'Product Name': submitDataRef.current?.new_product_name,
        'Product Code': submitDataRef.current?.product_code,
        'Product Cost': submitDataRef.current?.product_cost,
        'Additional Cost': submitDataRef.current?.additional_cost,
        'Product Barcode': submitDataRef.current?.product_barcode,
        'Category Name': submitDataRef.current?.category?.label,
        'Brand Name': submitDataRef.current?.brand?.label,
      })

      navigate('/products')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess])

  useEffect(() => {
    if (isError) {
      trackEventInternal(IntercomEvent['add-product-failed'])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError])

  console.log('error', error)

  return (
    <ProductSourceBodyWrapper title="Add Single Product">
      <Formik
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={yup.object({
          new_product_name: yup
            .string()
            .required('Product name is a required.'),
          category: yup.object().required('Category is required'),
          brand: yup.object().required('Brand is required'),
        })}
      >
        {({
          resetForm,
          setFieldValue,
          setFieldTouched,
        }: FormikHelpers<any>) => (
          <Form className="w-full">
            <div className="grid gap-6 md:grid-cols-2 font-medium text-sm text-gray-700 pb-8">
              <FormElement label="Product Name" name="new_product_name">
                <TextInput
                  name="new_product_name"
                  placeholder="Enter product name"
                />
              </FormElement>
              <FormElement label="Product Code" name="product_code" optional>
                <TextInput
                  name="product_code"
                  placeholder="Enter product code"
                />
              </FormElement>
              <FormElement label="Category" name="category">
                <CategoriesDropdown
                  name="category"
                  onChange={(category: DropdownOption) => {
                    setSelectedCategory(category)
                  }}
                  isCreatable={true}
                  value={selectedCategory}
                />
              </FormElement>
              <FormElement label="Brand" name="brand">
                <BrandsDropdown
                  name="brand"
                  onChange={(brand: DropdownOption) => {
                    setSelectedBrand(brand)
                  }}
                  isCreatable={true}
                  value={selectedBrand}
                />
              </FormElement>
              <FormElement label="Tags" optional>
                <TagsDropdown
                  name="tags"
                  onChange={(tag: DropdownOption[]) => {
                    setSelectedTags(tag)
                  }}
                  value={selectedTags}
                  isMulti
                />
              </FormElement>
              <FormElement label="Barcode" optional>
                <TextInput
                  name="product_barcode"
                  placeholder="Enter barcode number"
                />
              </FormElement>
              <FormElement label="Product Cost" optional>
                <Currency name="product_cost" placeholder="799,00" />
              </FormElement>
              <FormElement label="Additional Cost" optional>
                <Currency name="additional_cost" placeholder="799,00" />
              </FormElement>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-between sm:space-x-3 space-y-3 sm:space-y-0">
              <button
                type="button"
                onClick={() =>
                  clearForm(resetForm, setFieldValue, setFieldTouched)
                }
                className="w-full border border-solid border-gray-300 py-2 font-normal text-base capitalize active:bg-gray-100 shadow-sm rounded-lg h-11 bg-white-100"
              >
                Clear
              </button>
              <button
                disabled={isLoading}
                type="submit"
                className="w-full border border-solid border-primary-600 active:bg-primary-700 shadow-sm rounded-lg bg-primary-600 h-11 disabled:opacity-75"
              >
                <label className="pt-2 font-normal text-base capitalize text-white-100">
                  Add Product
                </label>
              </button>
            </div>
            {isError && error ? (
              <p className="text-left mt-3 mb-0 p-0 !text-red-500">
                A product with the same Name, Brand, and Category already
                exists. Please review the existing product instead of creating a
                duplicate.
              </p>
            ) : null}
          </Form>
        )}
      </Formik>
    </ProductSourceBodyWrapper>
  )
}

export default AddProductsManuallySingle
